import { useSelector } from 'react-redux'

import { useAppSelector } from '../../../../store/hooks'
import { isMicrosoftPayment } from '../../../../store/auth-company-slice'
import { roleAdmin, selectAuthUserSlice } from '../../../../store/auth-user-slice'
import { useLocationAdmin } from '../../../../hooks/use-location-admin'
import { IUserFilteredInfo } from '../../../../types/users'

export interface IUsersPagePermissions {
  amIAdmin: boolean
  isApprover: boolean
  isFullAdmin: boolean
  isLocationAdmin: boolean
  isMicrosoftBillingPayment: boolean
  managedLocationIds: string[]
  canEditUser: (row: IUserFilteredInfo) => boolean
  canEditCell: (row: IUserFilteredInfo, columnId: string) => boolean
  getLockReason: (row: IUserFilteredInfo, columnId: string) => string | undefined
}

export function usePermissions(): IUsersPagePermissions {
  const { isLocationAdmin, managedLocationIds } = useLocationAdmin()
  const {
    authUser: { id: authUserId, approverToTeams },
  } = useAppSelector(selectAuthUserSlice)
  const isMicrosoftBillingPayment = useSelector(isMicrosoftPayment)
  const amIAdmin = useSelector(roleAdmin)
  // A combo's role is 'LocationAdmin' by precedence, so a role-based approver check misses it; detect the
  // approver dimension via approverToTeams. Filter scoping: plain approvers see only their departments;
  // location admins (incl. combo) see only their locations but every department; full admins see all.
  const isApprover = (approverToTeams?.length ?? 0) > 0
  const isFullAdmin = amIAdmin && !isLocationAdmin
  // A location admin who is also an approver can VIEW approver-team users (returned by the backend),
  // but may only EDIT users in their managed locations. Full admins can edit anyone.
  const canEditUser = (row: IUserFilteredInfo) => amIAdmin && (row.id === authUserId || !isLocationAdmin || managedLocationIds.includes(row.location?.id))

  const getLockReason = (row: IUserFilteredInfo, columnId: string): string | undefined => {
    if (!amIAdmin) {
      return 'users.table.lockNoPermission'
    }
    if (row.status === 'DELETED') {
      return 'users.table.lockDeletedView'
    }
    if (row.id === authUserId) {
      return 'users.table.lockSelfRow'
    }
    if (row.role === 'Admin') {
      return 'users.table.lockAdminRow'
    }
    if (isLocationAdmin && !managedLocationIds.includes(row.location?.id)) {
      return 'users.table.lockOutOfScopeLocation'
    }
    if (columnId === 'location' && isLocationAdmin) {
      return 'users.table.lockLocationField'
    }
    if (columnId === 'labels' && isLocationAdmin) {
      return 'users.table.lockLabelsField'
    }
    return undefined
  }

  const canEditCell = (row: IUserFilteredInfo, columnId: string) => !getLockReason(row, columnId)

  return {
    amIAdmin,
    isApprover,
    isFullAdmin,
    isLocationAdmin,
    isMicrosoftBillingPayment,
    managedLocationIds,
    canEditUser,
    canEditCell,
    getLockReason,
  }
}
